<script setup lang="ts">
import { useStorage } from "@vueuse/core";
import { ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  EntityDefinition,
  EntityDefinitionInheritance,
  EntityDefinitionListDto,
  EntityDefinitionStatus,
  FormType,
} from "~/api";
import EntityDefinitionDetailEditor from "~/Compliance/components/EntityDefinitionDetailEditor.vue";
import FormDesigner from "~/NewFormBuilder/components/Designer/FormDesigner.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth, emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

type mainType = EntityDefinition;
type listItemType = EntityDefinitionListDto;

const myInfoStore = useMyInfoStore();

const loading = ref(true);
const drawerOpen = ref(true);
const props = defineProps<{
  entityDefinitionListDto: listItemType;
}>();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: mainType): void;
  (e: "added", value: mainType): void;
  (e: "deleted", value: mainType): void;
}>();

const isUsable = computed(() => {
  return entityDefinition.value.guid !== emptyGuid;
});

const entityDefinition = ref<mainType>({
  guid: "",
  sysOrgGuid: "",
  formDefinitionGuid: "",
  name: "",
  inheritance: EntityDefinitionInheritance.INHERITED,
  namePlural: "",
  status: EntityDefinitionStatus.UNUSED,
});
const lastSavedJson = ref<string>("");

const activeEntityTab = useStorage("activeEntityTab", "general");

// const form = ref<FormDefinition>();
// const rendererKey = ref(0);

// watch(
//   () => form.value,
//   () => {
//     rendererKey.value++;
//   },
// );

const updateLastUpdated = () => {
  lastSavedJson.value = JSON.stringify(entityDefinition.value);
};

onMounted(async () => {
  const guidWanted = props.entityDefinitionListDto.guid;
  if (guidWanted?.startsWith("new") || !guidWanted) {
    activeEntityTab.value = "general";

    // create a new entity definition
    entityDefinition.value = {
      guid: emptyGuid,
      sysOrgGuid: emptyGuid,
      formDefinitionGuid: emptyGuid,
      name: "",
      inheritance: EntityDefinitionInheritance.INHERITED,
      namePlural: "",
      status: EntityDefinitionStatus.UNUSED,
      formDefinition: {
        guid: emptyGuid,
        name: "Default",
        sysOrgGuid: emptyGuid,
        formType: FormType.ENTITY_DEFINITION,
        metaInfo: { multipleInstancesAllowed: true },
        rules: [],
        sectionRules: [],
        formRules: [],
        components: [],
      },
    };
    updateLastUpdated();
    loading.value = false;
    return;
  }
  apiClient.entityDefinition
    .getEntityDefinition(guidWanted, myInfoStore.currentSysOrgGuid)
    .then((res) => {
      entityDefinition.value = res;
      updateLastUpdated();
      loading.value = false;
    })
    .catch((error) => {
      showError("Error fetching entity definition:", error);
      loading.value = false;
    });
});

/** 'true' if any changes have been made, and a save is needed */
const saveNeeded = computed(() => {
  const changed =
    JSON.stringify(entityDefinition.value) !== lastSavedJson.value;

  return changed;
});

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
      })
      // If the user cancels
      .catch(() => {
        emit("closeDrawer");
      });
  } else {
    emit("closeDrawer");
  }
};

const handleNewEntityDefinitionAdded = (entityDef: mainType) => {
  entityDefinition.value = entityDef;
  updateLastUpdated();
  emit("added", entityDef);
};

const handleEntityDefinitionUpdated = (entityDef: mainType) => {
  // passthrough
  entityDefinition.value = entityDef;
  updateLastUpdated();
  emit("updated", entityDef);
};

const handleEntityDefinitionDeleted = (entityDef: mainType) => {
  entityDefinition.value = entityDef;
  emit("deleted", entityDef);
};
</script>

<template>
  <el-drawer
    v-if="entityDefinition"
    :model-value="drawerOpen"
    :title="entityDefinition.name || '\u00A0'"
    :before-close="handleClose"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
    class="entity-definition-drawer"
  >
    <div v-loading="loading" class="item-designer">
      <el-tabs v-model="activeEntityTab" type="card" class="demo-tabs">
        <el-tab-pane label="General" name="general">
          <EntityDefinitionDetailEditor
            v-if="entityDefinition"
            :model-value="entityDefinition"
            @added="handleNewEntityDefinitionAdded"
            @updated="handleEntityDefinitionUpdated"
            @deleted="handleEntityDefinitionDeleted"
          />
        </el-tab-pane>
        <el-tab-pane v-if="isUsable" label="Form" name="design" lazy>
          <div v-if="entityDefinition?.formDefinition" class="formContainer">
            <FormDesigner
              v-model="entityDefinition.formDefinition"
              :compliance-item="true"
              @form-saved="updateLastUpdated"
            />
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>
<style lang="less">
.entity-definition-drawer {
  .renderer {
    display: grid;
    grid-template-areas:
      "renderer-inner edit edit"
      "renderer-inner details security";
    grid-template-columns: 2fr 1fr 1fr;
    gap: 1em;
    border-left: 1px solid #ebeef5;

    table {
      width: 100%;
      td {
        padding: 0.5em;
        border-bottom: 1px solid #ebeef5;
      }
    }
  }
}
</style>
